import React, { useState, useEffect, useCallback } from "react";

import { Table, TableProps, Card, Row, Col, Badge, Tag, Typography, Space, Button } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import { Link } from "react-router-dom";
import { format } from "date-fns";

import SessionStore from "../stores/SessionStore";

const { Text, Title } = Typography;

export type GetPageCallbackFunc = (totalCount: number, rows: object[]) => void;

interface IProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnsType<any>;
  getPage: (
    limit: number,
    offset: number,
    filters: object,
    orderBy: string | void,
    orderByDesc: boolean | void,
    callbackFunc: GetPageCallbackFunc,
  ) => void;
  onRowsSelectChange?: (ids: string[]) => void;
  onChange?: (pagination: any, filters: any, sorter: any) => void;
  rowKey: string;
  refreshKey?: unknown;
  noPagination?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderCard?: (record: any) => React.ReactNode;
}

function ResponsiveDataTable(props: IProps) {
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(SessionStore.getRowsPerPage());
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [orderBy, setOrderBy] = useState<string>("");
  const [orderByDesc, setOrderByDesc] = useState<boolean>(false);
  const [filters, setFilters] = useState<object>({});
  const [rows, setRows] = useState<object[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

  const loadPage = useCallback(
    (page: number, pz: number, filters: object, orderBy?: string | void, orderByDesc?: boolean | void) => {
      setLoading(true);

      // If totalCount is known and less than 100, load all items
      const limit = (totalCount > 0 && totalCount < 100) ? totalCount : pz;
      const offset = (totalCount > 0 && totalCount < 100) ? 0 : (page - 1) * pz;

      props.getPage(limit, offset, filters, orderBy, orderByDesc, (totalCount: number, rows: object[]) => {
        setTotalCount(totalCount);
        setRows(rows);
        setLoading(false);
      });
    },
    [props, pageSize, totalCount],
  );

  const onRowsSelectChange = (ids: React.Key[]) => {
    const idss = ids as string[];
    if (props.onRowsSelectChange) {
      props.onRowsSelectChange(idss);
    }
  };

  const onChange: TableProps<object>["onChange"] = (pagination, filters, sorter, _) => {
    let page = pagination.current;
    if (!page) {
      page = currentPage;
    }

    let pz = pagination.pageSize;
    if (!pz) {
      pz = pageSize;
    }
    SessionStore.setRowsPerPage(pz);

    let firstSorter: SorterResult<object> | void = undefined;
    if (Array.isArray(sorter)) {
      if (sorter.length) {
        firstSorter = sorter[0];
      }
    } else {
      firstSorter = sorter;
    }
    let sort: string | void = undefined;
    if (firstSorter) {
      if (firstSorter.columnKey) {
        sort = firstSorter.columnKey.toString();
        if (firstSorter.order === "descend") {
          setOrderByDesc(true);
        } else {
          setOrderByDesc(false);
        }
      }
    }
    if (!sort) {
      sort = orderBy;
    }

    setCurrentPage(page);
    setPageSize(pz || 0);
    setOrderBy(sort);
    setFilters(filters);

    // Call parent onChange if provided
    if (props.onChange) {
      props.onChange(pagination, filters, sorter);
    }
  };

  useEffect(() => {
    loadPage(currentPage, pageSize, filters, orderBy, orderByDesc);
  }, [props, currentPage, pageSize, filters, orderBy, orderByDesc, loadPage]);

  // Reload all items when totalCount changes and is less than 100
  useEffect(() => {
    if (totalCount > 0 && totalCount < 100) {
      loadPage(1, totalCount, filters, orderBy, orderByDesc);
    }
  }, [totalCount, filters, orderBy, orderByDesc, loadPage]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  const { getPage, refreshKey, renderCard, ...otherProps } = props;
  let loadingProps = undefined;
  if (loading) {
    loadingProps = {
      delay: 300,
    };
  }

  // Only show pagination for 100+ items
  let pagination = undefined;
  if (totalCount >= 100 && (props.noPagination === undefined || props.noPagination === false)) {
    pagination = {
      current: currentPage,
      total: totalCount,
      pageSize: pageSize,
      showSizeChanger: true,
    };
  }

  let rowSelection = undefined;
  if (props.onRowsSelectChange) {
    rowSelection = {
      onChange: onRowsSelectChange,
    };
  }

  // Default card renderer if none provided
  const defaultRenderCard = (record: any) => {
    const columns = props.columns.filter(col => col.key !== 'state' && col.key !== 'battery');
    
    return (
      <Card 
        key={record[props.rowKey]} 
        size="small" 
        style={{ marginBottom: 8 }}
        bodyStyle={{ padding: 12 }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {columns.map((col: any) => {
            if (!col.render && !record[col.dataIndex]) return null;
            
            const value = col.render ? col.render(record[col.dataIndex], record) : record[col.dataIndex];
            if (!value) return null;
            
            return (
              <div key={col.key}>
                <Text strong style={{ fontSize: 12, color: '#666' }}>
                  {col.title}:
                </Text>
                <br />
                <Text style={{ fontSize: 14 }}>
                  {value}
                </Text>
              </div>
            );
          })}
        </Space>
      </Card>
    );
  };

  if (isMobile) {
    return (
      <div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            Loading...
          </div>
        ) : (
          <div>
            {rows.map((record: any) => 
              renderCard ? renderCard(record) : defaultRenderCard(record)
            )}
            {pagination && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Table
                  dataSource={[]}
                  pagination={pagination}
                  onChange={onChange}
                  {...otherProps}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <Table
      loading={loadingProps}
      dataSource={rows}
      pagination={pagination || false}
      rowSelection={rowSelection}
      onChange={onChange}
      scroll={{ x: 'max-content' }}
      {...otherProps}
    />
  );
}

export default ResponsiveDataTable;